import { createFileRoute } from "@tanstack/react-router";
import { tabSearch } from "@/app/tabs";
import { PendingSurface } from "@/components/feedback/pending-surface";
import { SettingsTabs, settingsSections, visibleSettingsSections } from "@/features/settings";
import { requirePerm } from "@/lib/auth/guards";

export const Route = createFileRoute("/_authenticated/settings")({
  // Retired `?tab=` ids resolve to the tab that absorbed them — `app/tabs.ts`.
  validateSearch: tabSearch("settings"),
  beforeLoad: requirePerm("EDIT_STAFF"),
  component: SettingsRoute,
});

/**
 * The tab strip over the section the address names. The strip is always drawn.
 *
 * AN ADDRESS WITH NO `?tab=` IS THE FIRST TAB (Rachel, 28-09-2026): the nav
 * link carries none, and clicking Settings lands on Pharmacy & branches rather
 * than on a pending page that only repeats the strip. That is the rule every
 * tabbed surface follows — the POC's own fallback (`DECISIONS.md` 21-09-2026:
 * "the surface decides what to draw (the POC: its first tab)"), and the one
 * `app/tabs.ts` promises ("the surface draws its own first tab"). The address
 * is NOT rewritten to carry the id: absent stays absent. This reverses the
 * 25-09-2026 render guide's "bare /settings stays the pending page".
 *
 * An id the registry does not hold still keeps the pending surface behind a
 * strip that names every section.
 *
 * `?tab=log` is this file's declaration of the access log. The section is
 * drawn from `features/settings/access-log-tab.tsx`; the read lives in
 * `features/settings/api/access-log.ts`.
 */
function SettingsRoute() {
  const { tab } = Route.useSearch();
  const { session } = Route.useRouteContext();
  const all = settingsSections.use();
  const sections = visibleSettingsSections(all, session);
  const section =
    sections.find((item) => item.id === tab) ??
    (tab === undefined || all.some((item) => item.id === tab) ? sections[0] : undefined);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SettingsTabs active={section?.id ?? ""} session={session} />
      {section ? <section.Component /> : <PendingSurface titleKey="nav.settings" />}
    </div>
  );
}
